import styled from 'styled-components';

import { Text } from '../../../../cdl/Text/Text';
import { Tooltip } from '../../../../cdl/Tooltip/Tooltip';
import { formatPrice } from '../../../../common/helpers/formatPrice.helper';
import { formatTotalPrice } from '../../../../common/helpers/formatTotal.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { IconAlertTriangle } from '../../../../common/icons/cdl/AlertTriangle';
import { IconReplace } from '../../../../common/icons/cdl/Replace';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { PriceVolumeSubline } from '../PriceVolumeSubline';

import type { LineItemComparisonCell } from './types/LineItemComparisonCell';
import { LineItemComparisonIndicator } from './types/LineItemComparisonIndicator';
import type { LineItemComparisonRow } from './types/LineItemComparisonRow';
import { LineItemComparisonRowType } from './types/LineItemComparisonRowType';

const CheapestLabel = styled.span`
    display: inline-flex;
    position: relative;
    isolation: isolate;
    margin-left: ${theme.space[6]};

    &::before {
        content: '';
        position: absolute;
        inset-block: 0;
        left: 50%;
        width: calc(100% + 2 * ${theme.space[6]});
        transform: translateX(-50%);
        border-radius: ${theme.radii[4]};
        background-color: ${theme.colors.positive.background};
        z-index: -1;
    }
`;

const ProductIndicator = styled.span`
    color: ${theme.colors.foreground.subtle};
    display: inline-flex;
    margin-top: ${theme.space[2]};
`;

const formatCellPrice = (cell: LineItemComparisonCell, rowType: LineItemComparisonRowType) => {
    if (!cell.price) {
        return '';
    }

    return rowType === LineItemComparisonRowType.SAMPLE_KIT ? formatTotalPrice(cell.price) : formatPrice(cell.price);
};

const formatCellTotal = (cell: LineItemComparisonCell, rowType: LineItemComparisonRowType) => {
    if (cell.total) {
        return formatTotalPrice(cell.total);
    }

    return formatCellPrice(cell, rowType);
};

interface PriceCellContentProps {
    cell: LineItemComparisonCell;
    row: LineItemComparisonRow;
}

export const PriceCellContent = ({ cell, row }: PriceCellContentProps) => {
    if (!cell.price) {
        return (
            <Box display="flex" justifyContent="flex-end" alignItems="flex-start" gap={8}>
                <Text fontWeight={600} color="foreground.subtle">
                    {Characters.EN_DASH}
                </Text>
            </Box>
        );
    }

    const textColor = cell.isCheapest ? 'positive.foreground' : undefined;
    const totalPrice = <Text color={textColor}>{formatCellTotal(cell, row.type)}</Text>;
    let indicatorLabel = translate('order.detail.lineItemComparison.equivalentToRequestedProduct');
    if (cell.indicator === LineItemComparisonIndicator.DIFFERENT_PRODUCT) {
        indicatorLabel = cell.productName
            ? translate('order.detail.lineItemComparison.differentFromRequestedProductWithName', {
                  productName: cell.productName,
              })
            : translate('order.detail.lineItemComparison.differentFromRequestedProduct');
    } else if (cell.productName) {
        indicatorLabel = translate('order.detail.lineItemComparison.equivalentProduct', {
            productName: cell.productName,
        });
    }

    const isDifferentProduct = cell.indicator === LineItemComparisonIndicator.DIFFERENT_PRODUCT;

    return (
        <Box display="grid" rowGap={4} justifyItems="end">
            <Box display="flex" justifyContent="flex-end" alignItems="flex-start" gap={8}>
                {cell.indicator ? (
                    <Tooltip label={indicatorLabel} side="top" align="center" showArrow>
                        <ProductIndicator>
                            {isDifferentProduct ? (
                                <IconAlertTriangle width={16} height={16} />
                            ) : (
                                <IconReplace width={16} height={16} />
                            )}
                        </ProductIndicator>
                    </Tooltip>
                ) : null}
                {cell.isCheapest ? <CheapestLabel>{totalPrice}</CheapestLabel> : totalPrice}
            </Box>
            {row.type === LineItemComparisonRowType.PRODUCT &&
            cell.price &&
            cell.offeredVolume !== undefined &&
            cell.offeredUnit ? (
                <PriceVolumeSubline price={cell.price} volume={cell.offeredVolume} unit={cell.offeredUnit} />
            ) : null}
        </Box>
    );
};
